import Vue from 'vue';
import VueRouter from 'vue-router';

import LocalAuthApi from '@/api/LocalAuthApi';
// eslint-disable-next-line import/no-cycle
import store from '@/store';
import CookieUtil from '@/utils/CookieUtil';

import HomeRoute from './routes/Home';
import LoginRoute from './routes/Login';
import LogoutRoute from './routes/Logout';
import SettingsRoute from './routes/Settings';

import MailRoute from './routes/Mail';
import InventoryRoute from './routes/Inventory';

import UnauthorizedRoute from './routes/Unauthorized';

Vue.use( VueRouter );

const router = new VueRouter( {
    // https://router.vuejs.org/guide/essentials/history-mode.html#example-server-configurations
    mode: 'history',
    base: process.env.BASE_URL,
    // https://router.vuejs.org/guide/advanced/transitions.html#per-route-transition
    routes: [
        HomeRoute,
        LoginRoute,
        LogoutRoute,
        SettingsRoute,
        MailRoute,
        InventoryRoute,
        UnauthorizedRoute,
    ],
} );

async function loginUser( next ) {
    try {
        await store.dispatch( 'User/login', {
            username: LocalAuthApi.getLocalUsername(),
            password: LocalAuthApi.getLocalPassword(),
        } );
    } catch ( error ) {
        console.error( 'Router: Failed to login user. Redirecting to Login. Error -', error );
        LocalAuthApi.removeAllLocalCredentials();
        next( { path: LoginRoute.path } );
        throw error;
    }
}

/**
 * Check to see if a route requires a given Role or Action, if it does then check to see if the logged-in user has that permission
 * @returns { Boolean }
 */
function isAuthorizedToGoToRoute( route ) {
    if ( route.meta.allowedActions || route.meta.allowedRoles ) {
        return !!route.meta?.allowedActions?.some( store.getters[ 'User/actionsForCurrentLocationHasAction' ] )
            || !!route.meta?.allowedRoles?.some( store.getters[ 'User/rolesForCurrentLocationHasRole' ] );
    }
    return true;
}
/**
 * Returns the first route in the Navbar to which a user has permissions,
 * Take them to purgatory as a fallback
 * @returns { Object } - a route
 */
function getNextAuthorizedRoute() {
    return [
        ...MailRoute.children,
        ...InventoryRoute.children,
        SettingsRoute,
    ].find( isAuthorizedToGoToRoute ) || UnauthorizedRoute;
}
function goToNextIfAuthorizedAndExists( to, from, next ) {
    if ( ( to.matched.length > 0 ) && isAuthorizedToGoToRoute( to ) ) {
        const persistableRoute = to.matched.find( ( route ) => route.meta.shouldPersistBetweenLogins );
        if ( persistableRoute ) {
            CookieUtil.setLandingPageCookie( persistableRoute.path );
        }
        next();
    } else if ( ( to.matched.length > 0 ) && !isAuthorizedToGoToRoute( to ) ) {
        // clear landing page cookie, let it set itself again naturally
        CookieUtil.deleteLandingPageCookie();
        // go to next authorized route, can't find one - go to purgatory
        next( getNextAuthorizedRoute() );
    } else {
        // Go 'home' if the last page was login and an unauthorized user is attempting to go to a guarded route
        next( {
            path: ( from.name === 'Login' )
                ? '/'
                : from.path,
        } );
    }
}

// Route Auth guard
// Inspired by: https://router.vuejs.org/en/advanced/meta.html
router.beforeEach( ( to, from, next ) => {
    // Variable to make it easier to toggle console logs when debugging
    const showRouteGuardWarnings = false;
    async function doRouting() {
        if ( showRouteGuardWarnings ) console.table( { to, from, next } );
        if ( to.matched.some( ( route ) => route.meta && route.meta.requiresAuth ) ) {
            if ( store.getters[ 'User/isLoggedIn' ] ) {
                if ( showRouteGuardWarnings ) console.warn( 'Route Guard ( Requires Auth ) - Already Logged In' );
                goToNextIfAuthorizedAndExists( to, from, next );
            } else if ( LocalAuthApi.hasLocalLoginCredentials() ) {
                if ( showRouteGuardWarnings ) console.warn( 'Route Guard ( Requires Auth ) - Attempting Auto Login' );
                try {
                    await ( async () => {
                        if ( showRouteGuardWarnings ) console.warn( 'Route Guard ( Requires Auth ) - Auto Login' );
                        await loginUser( next );
                        goToNextIfAuthorizedAndExists( to, from, next );
                    } )();
                } catch ( error ) {
                    if ( showRouteGuardWarnings ) console.warn( 'Route Guard ( Requires Auth ) - Auto Login Failed', error );
                    next( { path: LoginRoute.path } );
                }
            } else {
                if ( showRouteGuardWarnings ) console.warn( 'Route Guard ( Requires Auth ) - Redirecting to Login Page' );
                next( { path: LoginRoute.path, query: { next: to.fullPath } } );
            }
        } else if ( to.matched.some( ( route ) => route.name === LoginRoute.name ) && LocalAuthApi.hasLocalLoginCredentials() ) {
            if ( showRouteGuardWarnings ) console.warn( 'Route Guard ( Login page as logged in User ) - Already Logged in, no login page for you' );
            next( { path: '/' } );
        } else if ( to.matched.length > 0 ) {
            if ( showRouteGuardWarnings ) console.warn( 'Route Guard ( No Auth Required ) - Not logged in, route exists, go next' );
            next();
        } else {
            if ( showRouteGuardWarnings ) console.warn( 'Route Guard ( No Auth Required ) - Not logged in, route doesnt exist, go to Login' );
            next( { path: LoginRoute.path } );
        }
    }

    // Do the routing!
    ( async () => {
        await doRouting();
    } )();
} );

// Update the document title on route change
// Idea from: https://stackoverflow.com/questions/51639850/how-to-change-page-titles-when-using-vue-router
router.afterEach( ( to ) => {
    const overridenRoutes = [ 'Login' ];

    // Remove 'Campus Store' verbiage if it exists
    const schoolVerbiage = store.state.Website.info
    && store.state.Website.info.siteTitle
    && store.state.Website.info.siteTitle.includes( 'Campus Store' )
        ? store.state.Website.info.siteTitle.replace( 'Campus Store', '' ).trim()
        : '';

    const siteTitle = schoolVerbiage !== ''
        ? `Logistics - ${ schoolVerbiage }`
        : 'Logistics';

    if ( overridenRoutes.includes( to.meta.documentTitle ) ) {
        document.title = siteTitle;
    } else {
        document.title = `${ to.meta.documentTitle } - ${ siteTitle }`;
    }
} );

export default router;
